// Game settings → General (spec/dashboard.md section 12.4): name and description saved on their own button (`PATCH` with
// `If-Match`, not the branch workspace), the slug and the id with Copy, the public key with Copy and Rotate… (typed
// slug), the Host switch card (M7 slice 3), Archive… (typed name, a soft delete) and, on an archived game, the banner with
// Restore.
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { ops } from '../../api/operations';
import { useAuth } from '../../auth/AuthContext';
import { useGame } from '../../shell/GameContext';
import { routes } from '../../shell/routes';
import { Button } from '../../ui/Button';
import { useConfirm } from '../../ui/ConfirmDialog';
import { Field } from '../../ui/Field';
import { TextArea, TextInput } from '../../ui/inputs';
import { formatInstantWithZone, useTimeZone } from '../../ui/time';
import { useToasts } from '../../ui/Toast';
import { queryCache } from '../../workspace/cache';
import { HostSwitchCard } from './HostSwitchCard';
import { asApiError } from './keys';
import { LoadError } from '../../ui/PageStates';
import { Archive, Copy, RefreshCw } from '../../ui/icons';

function CopyButton({ value, label }: { value: string; label: string }) {
  const toasts = useToasts();
  return (
    <Button
      size="s"
      icon={<Copy aria-hidden="true" />}
      aria-label={`Copy ${label}`}
      onClick={() => {
        navigator.clipboard?.writeText(value).then(
          () => toasts.success(`${label} copied.`),
          () => toasts.error('Could not copy; select the text and copy it by hand.'),
        );
      }}
    >
      Copy
    </Button>
  );
}

export function GeneralTab() {
  const { org, game, missing, offsets } = useGame();
  const { client } = useAuth();
  const toasts = useToasts();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [zone] = useTimeZone();
  const [name, setName] = useState(game.name);
  const [description, setDescription] = useState(game.description ?? '');
  const [busy, setBusy] = useState<'save' | 'rotate' | 'archive' | 'restore' | null>(null);
  const [error, setError] = useState<ReturnType<typeof asApiError> | null>(null);
  const blocked = missing('manage_settings');
  const archived = Boolean(game.archivedAt);
  const path = { org, game: game.slug };
  const refresh = () => queryCache.invalidate(`game:${org}/${game.slug}`);

  // the shell re-read the game (after a save or someone else's write): the form follows
  useEffect(() => {
    setName(game.name);
    setDescription(game.description ?? '');
  }, [game.name, game.description, game.version]);

  const dirty = name !== game.name || description !== (game.description ?? '');

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setBusy('save');
    setError(null);
    try {
      await client.call(ops.updateGame, { path, body: { name: name.trim(), description }, ifMatch: Number(game.version) });
      toasts.success('Game settings saved.');
      refresh();
    } catch (err) {
      const api = asApiError(err);
      if (api.isVersionConflict) {
        toasts.error('Someone changed the game meanwhile; the settings were re-read.', { requestId: api.requestId });
        refresh();
      } else setError(api);
    } finally {
      setBusy(null);
    }
  };

  const rotate = async () => {
    const ok = await confirm({ title: 'Rotate the public key?', body: 'Builds carrying the old key stop authenticating.', confirmLabel: 'Rotate', tone: 'danger', typeToConfirm: game.slug });
    if (!ok) return;
    setBusy('rotate');
    try {
      await client.call(ops.rotateGamePublicKey, { path });
      toasts.success('Public key rotated. Ship the new key with the next build.');
      refresh();
    } catch (err) {
      const api = asApiError(err);
      toasts.error(api.message, { requestId: api.requestId });
    } finally {
      setBusy(null);
    }
  };

  const archive = async () => {
    const ok = await confirm({ title: `Archive ${game.name}?`, body: 'The game disappears from the lists; restore it from the archived list.', confirmLabel: 'Archive', tone: 'danger', typeToConfirm: game.name });
    if (!ok) return;
    setBusy('archive');
    try {
      await client.call(ops.archiveGame, { path });
      toasts.success(`${game.name} archived.`);
      queryCache.invalidate(`games:${org}`);
      refresh();
      navigate(routes.games(org));
    } catch (err) {
      const api = asApiError(err);
      toasts.error(api.message, { requestId: api.requestId });
    } finally {
      setBusy(null);
    }
  };

  const restore = async () => {
    setBusy('restore');
    try {
      await client.call(ops.restoreGame, { path });
      toasts.success(`${game.name} restored.`);
      queryCache.invalidate(`games:${org}`);
      refresh();
    } catch (err) {
      const api = asApiError(err);
      toasts.error(api.message, { requestId: api.requestId });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="settings-general" className="settings-general">
      {archived && (
        <div className="banner banner-warning" role="status" data-testid="archived-banner">
          <Archive className="banner-icon" aria-hidden="true" />
          <div className="banner-body">This game was archived {formatInstantWithZone(game.archivedAt, zone, { offsets })}. It is hidden from the lists and its settings cannot change until it is restored.</div>
          <Button variant="primary" size="s" onClick={() => void restore()} busy={busy === 'restore'} missingPermission={blocked}>
            Restore
          </Button>
        </div>
      )}
      <form className="pane settings-form" onSubmit={(e) => void save(e)} aria-label="Name and description">
        <div className="pane-head">
          <h2>Name and description</h2>
        </div>
        <div className="pane-body">
          <div className="form-grid">
            <Field label="Name" required error={error?.fieldMessage('name')}>
              <TextInput aria-label="Name" value={name} onChange={(e) => setName(e.target.value)} required disabled={Boolean(blocked) || archived} title={blocked ? `You lack the '${blocked}' permission.` : undefined} />
            </Field>
            <Field className="span-2" label="Description" error={error?.fieldMessage('description')}>
              <TextArea aria-label="Description" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} disabled={Boolean(blocked) || archived} title={blocked ? `You lack the '${blocked}' permission.` : undefined} />
            </Field>
          </div>
          {error && !error.details?.length && <LoadError>{error.message}</LoadError>}
        </div>
        <div className="pane-foot">
          <Button type="submit" variant="primary" busy={busy === 'save'} disabled={!dirty || !name.trim() || archived} missingPermission={blocked}>
            Save
          </Button>
          {dirty && (
            <Button
              variant="ghost"
              onClick={() => {
                setName(game.name);
                setDescription(game.description ?? '');
                setError(null);
              }}
            >
              Discard
            </Button>
          )}
        </div>
      </form>

      <div className="pane settings-form">
        <div className="pane-head">
          <h2>Identity</h2>
        </div>
        <div className="pane-body">
          <dl className="kv settings-ids">
            <dt>Slug</dt>
            <dd className="row">
              <code data-testid="game-slug">{game.slug}</code>
              <CopyButton value={game.slug} label="Slug" />
            </dd>
            <dt>Id</dt>
            <dd className="row">
              <code data-testid="game-id">{game.id}</code>
              <CopyButton value={game.id} label="Id" />
            </dd>
          </dl>
          <div className="settings-key-block">
            <div className="field-label">Public key</div>
            <div className="secret-row">
              <pre data-testid="public-key" className="settings-public-key">
                {game.publicKey}
              </pre>
              <CopyButton value={game.publicKey} label="Public key" />
              <Button size="s" variant="danger" icon={<RefreshCw aria-hidden="true" />} onClick={() => void rotate()} busy={busy === 'rotate'} missingPermission={blocked}>
                Rotate…
              </Button>
            </div>
            <p className="field-help">The public key ships inside the game builds; the SDK sends it with every player request. It is not a secret.</p>
          </div>
        </div>
      </div>

      <HostSwitchCard />

      {!archived && (
        <div className="pane settings-danger">
          <div className="pane-head">
            <h2>Archive</h2>
          </div>
          <div className="pane-body settings-danger-body">
            <p>The game disappears from the lists and its players stop authenticating; nothing is deleted, and it can be restored from the archived list of the games page.</p>
            <Button variant="danger" icon={<Archive aria-hidden="true" />} onClick={() => void archive()} busy={busy === 'archive'} missingPermission={blocked}>
              Archive…
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
